export interface RoomAccessControlsProps {
  isClosed: boolean
  opensAt: string | null
  scheduledOpening: string
  isSaving: boolean
  onScheduledOpeningChange(value: string): void
  onOpen(): void
  onCloseIndefinitely(): void
  onCloseUntil(): void
}

function formatOpeningTime(opensAt: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(opensAt))
}

export function RoomAccessControls({
  isClosed,
  opensAt,
  scheduledOpening,
  isSaving,
  onScheduledOpeningChange,
  onOpen,
  onCloseIndefinitely,
  onCloseUntil,
}: RoomAccessControlsProps) {
  const scheduledTime = Date.parse(scheduledOpening)
  const canSchedule = Number.isFinite(scheduledTime) && scheduledTime > Date.now()

  return (
    <section className="flex flex-col gap-3" aria-labelledby="room-access-heading">
      <div>
        <h3 id="room-access-heading" className="text-xs font-bold text-muted uppercase tracking-wider">
          Room access
        </h3>
        <p className="mt-1 text-xs text-secondary" aria-live="polite">
          {isClosed
            ? opensAt
              ? `Closed until ${formatOpeningTime(opensAt)}`
              : 'Closed until you open it'
            : 'Open to anyone with the link'}
        </p>
      </div>

      {isClosed && (
        <button
          type="button"
          onClick={onOpen}
          disabled={isSaving}
          className="w-full rounded border border-basic bg-surface px-3 py-2 text-sm font-semibold text-primary hover:bg-subtle disabled:opacity-50"
        >
          Open room now
        </button>
      )}

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onCloseIndefinitely}
          disabled={isSaving || (isClosed && !opensAt)}
          className="w-full rounded border border-basic bg-surface px-3 py-2 text-sm font-semibold text-primary hover:bg-subtle disabled:opacity-50"
        >
          Close indefinitely
        </button>

        <label className="text-xs font-semibold text-secondary" htmlFor="room-opens-at">
          Or open automatically at
        </label>
        <div className="flex gap-2">
          <input
            id="room-opens-at"
            type="datetime-local"
            value={scheduledOpening}
            onChange={(event) => onScheduledOpeningChange(event.target.value)}
            className="min-w-0 flex-1 rounded border border-basic bg-surface px-2 py-2 text-sm text-primary focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <button
            type="button"
            onClick={onCloseUntil}
            disabled={isSaving || !canSchedule}
            className="rounded bg-accent px-3 py-2 text-sm font-semibold text-inverse hover:brightness-110 disabled:opacity-50"
          >
            Set
          </button>
        </div>
      </div>
    </section>
  )
}
